import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Modal, Form, Input, InputNumber, DatePicker, TimePicker, message, List, Tag, Space, Empty, Popconfirm, Progress } from 'antd';
import { TrophyOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, MailOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import NewConvocationModal from './NewConvocationModal';

const BRAND = '#25D366';

function fmtDate(v) {
  if (!v) return '—';
  return dayjs(v).format('DD/MM/YYYY');
}
function fmtTime(t) {
  if (!t) return null;
  return String(t).slice(0, 5);
}

// Retorna true se o jogo é FUTURO (comparando data+hora com now).
// Aceita match_date=YYYY-MM-DD e match_time=HH:MM:SS
function isFutureMatch(m) {
  if (!m.match_date) return false;
  const t = m.match_time ? String(m.match_time).slice(0, 5) : '20:00';
  const dt = dayjs(`${m.match_date} ${t}`, 'YYYY-MM-DD HH:mm');
  return dt.isValid() && dt.isAfter(dayjs().subtract(5, 'minute'));
}

export default function TeamMatchesPanel({ teamId, teamName: teamNameProp }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [convocations, setConvocations] = useState([]);
  const [convByMatch, setConvByMatch] = useState({}); // { "manual:12": convId }
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [convModal, setConvModal] = useState({ open: false, match: null });
  const [form] = Form.useForm();
  const [teamName, setTeamName] = useState(teamNameProp || '');

  useEffect(() => {
    if (teamNameProp || !teamId) return;
    api.get('/community/team-owner/my')
      .then(r => {
        const t = (r.data?.teams || []).find(x => x.id === teamId);
        if (t?.name) setTeamName(t.name);
      })
      .catch(() => {});
  }, [teamId, teamNameProp]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/community/teams/${teamId}/matches`).then(r => r.data?.matches || []).catch(() => []),
      api.get(`/community/convocations/team/${teamId}`).then(r => r.data?.convocations || []).catch(() => []),
      api.get(`/community/convocations/team/${teamId}/by-match`).then(r => r.data?.by_match || {}).catch(() => ({})),
    ]).then(([m, c, byMatch]) => {
      setMatches(m);
      setConvocations(c);
      setConvByMatch(byMatch);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { if (teamId) load(); }, [teamId]);

  const openNew = () => {
    form.resetFields();
    setModal({ open: true, editing: null });
  };

  const openEdit = (m) => {
    if (m.source !== 'manual') {
      message.info('Jogos vindos do Futezap são editados na tela de Jogos');
      return;
    }
    form.setFieldsValue({
      opponent_name: m.opponent_name,
      match_date: m.match_date ? dayjs(m.match_date) : null,
      match_time: m.match_time ? dayjs(m.match_time, 'HH:mm:ss') : null,
      quadra: m.quadra,
      score_home: m.score_home,
      score_away: m.score_away,
      notes: m.notes,
    });
    setModal({ open: true, editing: m });
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        match_date: v.match_date ? v.match_date.format('YYYY-MM-DD') : null,
        match_time: v.match_time ? v.match_time.format('HH:mm:ss') : null,
      };
      if (modal.editing) {
        await api.patch(`/community/matches/${modal.editing.id}`, payload);
      } else {
        await api.post(`/community/teams/${teamId}/matches`, payload);
      }
      message.success('Jogo salvo');
      setModal({ open: false, editing: null });
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Erro');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/community/matches/${id}`);
      message.success('Removido');
      load();
    } catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
  };

  const openConvocationForMatch = (m) => {
    setConvModal({
      open: true,
      match: {
        source: m.source, // 'manual' | 'game'
        id: m.id,
        title: `${teamName || 'Nosso time'} vs ${m.opponent_name || '—'}`,
        date: m.match_date,
        time: m.match_time,
        quadra: m.quadra,
        city: null,
      },
    });
  };

  const openConvocations = convocations.filter(c => c.status === 'sent');
  const isEmpty = !loading && matches.length === 0 && convocations.length === 0;

  return (
    <>
      <Card style={{ borderRadius: 16, marginBottom: 16 }}
        title={<><TrophyOutlined /> Jogos</>}
        extra={
          <Button size="small" type="default" icon={<PlusOutlined />} onClick={openNew}>
            Adicionar jogo
          </Button>
        }>

        {/* Banner promo quando totalmente vazio */}
        {isEmpty && (
          <div style={{
            background: 'linear-gradient(135deg, #075E54 0%, #128C7E 50%, #25D366 100%)',
            borderRadius: 12, padding: 24, color: '#fff', marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🆕</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              Convoque seu time em 30 segundos
            </div>
            <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 16, maxWidth: 380, margin: '0 auto 16px' }}>
              Adicione o próximo jogo aqui e envie convocação por email pros jogadores confirmarem presença.
              Você ganhou <strong>20 créditos grátis</strong> pra testar. WhatsApp e Telegram em breve.
            </div>
            <Button size="large" icon={<PlusOutlined />} onClick={openNew}
              style={{ background: '#fff', color: '#075E54', fontWeight: 700, border: 'none' }}>
              Adicionar primeiro jogo
            </Button>
          </div>
        )}

        {/* Convocações abertas */}
        {openConvocations.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              <ClockCircleOutlined /> Convocações abertas
            </div>
            <List
              dataSource={openConvocations}
              renderItem={c => {
                const total = (c.going || 0) + (c.not_going || 0) + (c.waiting || 0) + (c.pending || 0);
                const respondedPct = total > 0 ? Math.round(((total - (c.pending || 0)) / total) * 100) : 0;
                return (
                  <List.Item
                    style={{ cursor: 'pointer', padding: 12, borderRadius: 8, background: '#f6ffed', marginBottom: 6 }}
                    onClick={() => navigate(`/time/convocacao/${c.id}`)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        <MailOutlined style={{ color: BRAND, marginRight: 6 }} />
                        {c.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6 }}>
                        {fmtDate(c.match_date)} · {fmtTime(c.match_time)}
                        {c.quadra ? ` · ${c.quadra}` : ''}
                        {' · '}
                        <Tag color="green" style={{ margin: 0, fontSize: 10 }}>✓ {c.going || 0}</Tag>
                        <Tag color="red" style={{ margin: '0 4px', fontSize: 10 }}>✗ {c.not_going || 0}</Tag>
                        <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>⏳ {c.waiting || 0}</Tag>
                        {c.pending > 0 && <Tag style={{ marginLeft: 4, fontSize: 10 }}>? {c.pending}</Tag>}
                      </div>
                      <Progress percent={respondedPct} size="small" showInfo={false} strokeColor={BRAND} />
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        {loading ? null : matches.length === 0 ? (
          openConvocations.length === 0 && !isEmpty ? (
            <Empty description="Sem jogos ainda" />
          ) : null
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              <TrophyOutlined /> Histórico
            </div>
            <List
              dataSource={matches}
              renderItem={m => {
                const hasScore = m.score_home != null && m.score_away != null;
                const won = hasScore && m.score_home > m.score_away;
                const drew = hasScore && m.score_home === m.score_away;
                const badgeColor = !hasScore ? 'default' : won ? 'green' : drew ? 'gold' : 'red';
                const badgeText = !hasScore ? 'sem placar' : won ? 'V' : drew ? 'E' : 'D';
                const future = !hasScore && isFutureMatch(m);
                // Convocação da comunidade é 100% isolada — só jogos manual entram.
                // Jogos vindos do Futezap Pro têm seu próprio fluxo n8n; não misturam aqui.
                const canConvoke = future && m.source === 'manual';
                const existingConvId = convByMatch[`${m.source}:${m.id}`];

                const actions = [];
                if (canConvoke) {
                  if (existingConvId) {
                    actions.push(
                      <Button key="view-conv" size="small" type="text"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/time/convocacao/${existingConvId}`)}
                        style={{ color: BRAND }}>
                        Ver convocação
                      </Button>
                    );
                  } else {
                    actions.push(
                      <Button key="conv" size="small"
                        icon={<ThunderboltOutlined />}
                        onClick={() => openConvocationForMatch(m)}
                        style={{ background: BRAND, borderColor: BRAND, color: '#fff' }}>
                        Convocar
                      </Button>
                    );
                  }
                }
                if (m.source === 'manual') {
                  actions.push(
                    <Button key="e" size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(m)} />,
                    <Popconfirm key="d" title="Remover?" onConfirm={() => remove(m.id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  );
                } else {
                  actions.push(<Tag key="src" color="cyan">via Futezap</Tag>);
                }

                return (
                  <List.Item actions={actions}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>
                        {teamName ? `${teamName} ` : ''}<span style={{ color: '#999', fontWeight: 400 }}>vs</span> {m.opponent_name || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {fmtDate(m.match_date)}
                        {fmtTime(m.match_time) ? ` · ${fmtTime(m.match_time)}` : ''}
                        {m.quadra ? ` · ${m.quadra}` : ''}
                      </div>
                      {m.notes && <div style={{ fontSize: 12, color: '#555', marginTop: 4, fontStyle: 'italic' }}>{m.notes}</div>}
                    </div>
                    <Space direction="vertical" align="end" size={2}>
                      {hasScore && <div style={{ fontSize: 18, fontWeight: 900 }}>{m.score_home}×{m.score_away}</div>}
                      {!future && <Tag color={badgeColor} style={{ margin: 0 }}>{badgeText}</Tag>}
                    </Space>
                  </List.Item>
                );
              }}
            />
          </>
        )}

        <Modal
          title={modal.editing ? 'Editar jogo' : 'Adicionar jogo'}
          open={modal.open}
          onCancel={() => setModal({ open: false, editing: null })}
          onOk={save}
          okText="Salvar" cancelText="Cancelar"
          width={480}
        >
          <Form form={form} layout="vertical">
            <Form.Item name="opponent_name" label="Adversário" rules={[{ required: true, message: 'Nome do adversário' }]}>
              <Input placeholder="Ex: Só Toca FC" maxLength={120} />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item name="match_date" label="Data" style={{ flex: 1 }}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="match_time" label="Hora" style={{ width: 120 }}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="quadra" label="Local / quadra">
              <Input maxLength={200} />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Form.Item name="score_home" label="Nossos gols" style={{ flex: 1 }}>
                <InputNumber min={0} max={99} style={{ width: '100%' }} />
              </Form.Item>
              <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>×</div>
              <Form.Item name="score_away" label="Gols adversário" style={{ flex: 1 }}>
                <InputNumber min={0} max={99} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="notes" label="Observações">
              <Input.TextArea rows={2} maxLength={500} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>

      <NewConvocationModal
        open={convModal.open}
        teamId={teamId}
        teamName={teamName}
        match={convModal.match}
        onClose={() => setConvModal({ open: false, match: null })}
        onCreated={(r) => { load(); navigate(`/time/convocacao/${r.convocation_id}`); }}
      />
    </>
  );
}
